/*
 * tcw-order-core.js — 下单区共享核心（商品页 + 试穿页抽屉 + Calculator 共用）
 *
 * 三层里的前两层，纯函数、不碰 DOM、不读 window：
 *   1. normalizeProduct(raw)            Shopify 商品 → 固定结构（能租/租价表、能买/购买价、订单类型、品类…）
 *   2. buildOrderView(p, s, ctx)         → 区块显隐 + 金额 + 缺填项
 *      buildCartLines(p, s, ctx)         → 加购时的购物车行与 properties
 * 第三层（怎么画）各页自管：商品页 Liquid 出 HTML、JS 照 view 显隐填数；试穿页按 blocks 用自己的模板画。
 *
 * 金额一律是「访客看到的货币」的主单位（如 HKD 16000、SGD 2661）。各页适配层负责换算后再传进来：
 *   商品页传 presentment 价 /100；试穿页传 HKD × Shopify.currency.rate。
 * 方案与盘点：docs/sprint-8/下单区统一-方案.md
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.TCWOrder = api;
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";
  var VERSION = 1;
  var MTO_VENDOR = "THE WEDDING GOWN";
  var CUST_TEXT_NEED = { name: 2, quote: 1 }; // 需填写文本的加价选项 key → 输入框数
  var DEFAULT_MEASURE_ROWS = [
    { nr: 1, label: "Bust Circumference", inc: true, def: 80, min: 50, max: 150 },
    { nr: 2, label: "Waist circumference", inc: true, def: 66, min: 40, max: 130 },
    { nr: 3, label: "Hip circumference", inc: true, def: 96, min: 60, max: 160 },
    { nr: 4, label: "Neck circumference", inc: false, min: 25, max: 50 },
    { nr: 5, label: "Shoulder width", inc: false, min: 30, max: 60 },
    { nr: 6, label: "Arm hole circumference", inc: false, min: 25, max: 60 },
    { nr: 7, label: "Arm length", inc: false, min: 40, max: 70 },
    { nr: 8, label: "waist to hem length", inc: false, min: 50, max: 160 },
    { nr: 9, label: "Back Shoulder Width", inc: false, min: 30, max: 60 },
    { nr: 10, label: "Back Waist Length", inc: false, min: 25, max: 60 },
    { nr: 11, label: "Center Back Length", inc: false, min: 100, max: 200 },
    { nr: 12, label: "Upper Arm", inc: false, min: 20, max: 60 },
    { nr: 13, label: "Forearm", inc: false, min: 15, max: 45 },
    { nr: 14, label: "Wrist", inc: false, min: 10, max: 30 },
  ];
  // 商品类型 → 品类。商品页按 product.type 判定（Wedding Top / Wedding Skirt / Headpiece / Accessory），
  // 试穿页的目录分类作为补充（ctx 里没有 type 时用）。
  var TYPE_KIND = { "wedding top": "top", "wedding skirt": "bottom", "headpiece": "headpiece", "accessory": "accessory" };
  var KIND_NOUN = { gown: "gown", top: "top", bottom: "skirt", headpiece: "veil", accessory: "accessory" };

  function num(v) { var n = Number(v); return isFinite(n) ? n : null; }
  function pos(v) { var n = num(v); return n != null && n > 0 ? n : null; }
  function cap(s) { s = String(s || ""); return s.charAt(0).toUpperCase() + s.slice(1); }
  function round(n) { return Math.round(Number(n) || 0); }

  // ---------------------------------------------------------------- 第 1 层：数据归一
  // raw: { id, title, handle, vendor, type, kind?, variants:[{id,title,price,compareAtPrice}],
  //        options?:[{key,label,upcharge}], fabricVariants?, sizeTable?, availability?, status?, condition? }
  // 规则（2026-10-04 定案，以商品页为准 + 修 Default Title）：
  //   - 标题含 Rent 的变体 = 租期方案；价格 > 0 才算数；天数取 "N day(s)"。基础租金 = 最便宜那档。
  //   - 其余变体（含 "Default Title"、"Sample sale"）= 购买价，取第一个价格 > 0 的。
  //   - 两样都没有 = 不能下单（noMode）。
  //   - raw.rentEnabled / raw.buyEnabled === false（商品开关关了）→ 当作没有该项。
  function normalizeProduct(raw) {
    raw = raw || {};
    var rent = [], buy = null;
    (raw.variants || []).forEach(function (v) {
      var title = String(v.title || "");
      var price = pos(v.price);
      if (/rent/i.test(title)) {
        if (price == null) return;
        var m = title.match(/(\d+)\s*day/i);
        rent.push({ id: v.id, title: title, price: price, days: m ? Number(m[1]) : null, term: m ? (Number(m[1]) === 1 ? "1 day" : m[1] + " days") : title.replace(/^rent\s*[·-]?\s*/i, "") });
      } else if (!buy && price != null) {
        var cmp = pos(v.compareAtPrice);
        buy = { id: v.id, title: title, price: price, compareAt: cmp != null && cmp > price ? cmp : null };
      }
    });
    // 10-06 owner: 数据 + 开关。开关(元字段 tcw.rental_enabled / tcw.sample_sale_enabled)明确为 false 才关，
    // 没设过当开；关了就当没有这项（分页、价格行都不出），数据本身不动。
    if (raw.rentEnabled === false) rent = [];
    if (raw.buyEnabled === false) buy = null;
    rent.sort(function (a, b) { return (a.days || 0) - (b.days || 0) || a.price - b.price; });
    var base = rent.length ? rent.reduce(function (m, v) { return v.price < m.price ? v : m; }, rent[0]) : null;
    var type = String(raw.type || "").trim();
    var kind = TYPE_KIND[type.toLowerCase()] || raw.kind || "gown";
    if (kind === "one-piece") kind = "gown";
    var mto = String(raw.vendor || "").trim().toUpperCase() === MTO_VENDOR;
    var noun = KIND_NOUN[kind] || "gown";
    return {
      v: VERSION,
      id: raw.id, title: raw.title || "", handle: raw.handle || "", vendor: raw.vendor || "", brand: raw.brand || raw.vendor || "",
      styleNo: raw.styleNo || null, type: type, kind: kind, noun: noun,
      rent: rent.length ? { variants: rent, base: base, baseDays: rent[0].days || null } : null,
      buy: buy ? { variant: buy, price: buy.price, compareAt: buy.compareAt, orderType: mto ? "Made To Order" : "Sample sale" } : null,
      noMode: !rent.length && !buy,
      mto: mto,
      canAlter: kind !== "headpiece" && kind !== "accessory",
      options: (raw.options || []).filter(function (o) { return o && o.key; }).map(function (o) { return { key: o.key, label: o.label || cap(o.key), upcharge: num(o.upcharge) || 0, textInputs: CUST_TEXT_NEED[o.key] || 0 }; }),
      sizeTable: raw.sizeTable || null,
      availability: raw.availability || "",
      status: raw.status || "", // 10-27: 每件商品自己的狀態/成色; 空 = 用 ctx 里的全店兜底
      condition: raw.condition || "",
    };
  }

  // 量体行：商品级可改区间（sizeTable.minAlter/maxAlter 的 bust/waist/hips）覆盖 1–3 行的通用区间。
  var ST_DIM = { 1: "bust", 2: "waist", 3: "hips" };
  function stNum(v) { var n = parseFloat(String(v == null ? "" : v).replace(/cm/i, "").trim()); return isFinite(n) ? n : null; }
  function stPick(o, d) { if (!o) return null; return stNum(o[d] != null ? o[d] : o[cap(d)]); }
  function measureRows(p, ctx) {
    var rows = (ctx && ctx.measureRows && ctx.measureRows.length) ? ctx.measureRows : DEFAULT_MEASURE_ROWS;
    var st = p && p.sizeTable;
    return rows.map(function (r) {
      var d = ST_DIM[r.nr]; if (!d || !st) return r;
      var mn = stPick(st.minAlter, d), mx = stPick(st.maxAlter, d);
      if (mn == null && mx == null) return r;
      var o = {}; for (var k in r) o[k] = r[k];
      if (mn != null) o.min = mn; if (mx != null) o.max = mx;
      return o;
    });
  }

  // 可选模式与当前模式。用户选的模式若不可用，落到可用的那个（只能买 → buy；只能租 → rent）。
  function modesOf(p) { return { rent: !!p.rent, buy: !!p.buy }; }
  function resolveMode(p, want) {
    var m = modesOf(p);
    if (!m.rent && !m.buy) return null;
    if (want === "buy" && m.buy) return "buy";
    if (want === "rent" && m.rent) return "rent";
    return m.rent ? "rent" : "buy";
  }
  function planOf(p, planId) {
    if (!p.rent) return null;
    var vs = p.rent.variants;
    for (var i = 0; i < vs.length; i++) if (String(vs[i].id) === String(planId)) return vs[i];
    return p.rent.base;
  }

  // ---------------------------------------------------------------- 第 2 层：视图模型
  // s（这一件的选择）: { mode:"rent"|"buy", planId, dates:{wedding,arrival,return,estArrival}, func,
  //   country(ISO), countryName, region, alts:{nr:{checked,value}}, opts:{key:true}, optText:{key:[..]},
  //   fabric:{label, upcharge} | null }
  // ctx（页面与店铺）: { today:"YYYY-MM-DD", depositPct(50), alterationPrice, measureRows, instOn,
  //   shipFee:function(iso, region) → number | null（null = 结账时再算）, statusValue, conditionValue, showStatus }
  function depositPctOf(iso, ctx) { var b = num(ctx && ctx.depositPct) || 50; return (!iso || iso === "HK" || iso === "TW") ? b : 100; }

  function quote(p, s, ctx) {
    s = s || {}; ctx = ctx || {};
    var mode = resolveMode(p, s.mode);
    var t = { mode: mode, base: 0, ext: 0, extraDays: 0, price: 0, alteration: 0, altItems: [], options: 0, optItems: [],
      fabric: 0, fabricLabel: "", shipping: 0, shipState: "idle", grand: 0, deposit: 0, depositPct: 0, total: 0, monthly: 0, plan: null };
    if (!mode) return t;
    if (mode === "rent") {
      var plan = planOf(p, s.planId);
      t.plan = plan; t.base = p.rent.base.price; t.price = plan.price; t.ext = plan.price - t.base;
      t.extraDays = (plan.days != null && p.rent.baseDays != null) ? plan.days - p.rent.baseDays : 0;
      t.depositPct = depositPctOf(s.country, ctx);
      t.deposit = round(t.base * t.depositPct / 100);
    } else {
      t.base = p.buy.price; t.price = p.buy.price;
    }
    if (p.canAlter) {
      var ap = num(ctx.alterationPrice) || 1000;
      measureRows(p, ctx).forEach(function (r) {
        var a = (s.alts || {})[r.nr];
        if (!r.inc && a && a.checked) { t.alteration += ap; t.altItems.push({ nr: r.nr, label: r.label, price: ap }); }
      });
    }
    p.options.forEach(function (o) { if ((s.opts || {})[o.key]) { t.options += o.upcharge; t.optItems.push(o); } });
    if (s.fabric && num(s.fabric.upcharge) > 0) { t.fabric = num(s.fabric.upcharge); t.fabricLabel = s.fabric.label || ""; }
    if (s.country) {
      var fee = typeof ctx.shipFee === "function" ? ctx.shipFee(s.country, s.region || "") : null;
      if (fee == null) t.shipState = "none"; else { t.shipState = "ok"; t.shipping = Number(fee) || 0; }
    }
    // 定案 10-04：总价含加价选项与面料加价（面料只在试穿页有）；押金不入 Grand Total，另出 Total。
    t.grand = t.price + t.alteration + t.options + t.fabric + t.shipping;
    t.total = t.grand + t.deposit;
    t.monthly = ctx.instOn && t.total > 0 ? t.total / 36 : 0;
    return t;
  }

  // 缺填项（加购前校验）。返回 [{field, reason}]，页面按 field 定位并出各自的提示文案。
  function missing(p, s, ctx) {
    s = s || {}; ctx = ctx || {};
    var out = [], mode = resolveMode(p, s.mode), d = s.dates || {};
    if (!mode) return [{ field: "mode", reason: "unavailable" }];
    if (mode === "rent") { if (!d.arrival) out.push({ field: "arrival", reason: "required" }); }
    else {
      if (!d.wedding) out.push({ field: "wedding", reason: "required" });
      if (!d.estArrival) out.push({ field: "estArrival", reason: "required" });
    }
    var today = ctx.today || "";
    if (today) (mode === "rent" ? ["wedding", "arrival", "return"] : ["wedding", "estArrival"]).forEach(function (k) {
      if (d[k] && d[k] < today) out.push({ field: k, reason: "past" });
    });
    if (!s.func) out.push({ field: "func", reason: "required" });
    if (!s.country) out.push({ field: "country", reason: "required" });
    if (p.canAlter) measureRows(p, ctx).forEach(function (r) {
      var a = (s.alts || {})[r.nr];
      if (!r.inc && a && a.checked && !String(a.value == null ? "" : a.value).trim()) out.push({ field: "alt:" + r.nr, reason: "required", label: r.label });
    });
    p.options.forEach(function (o) {
      if (!o.textInputs || !(s.opts || {})[o.key]) return;
      var tx = ((s.optText || {})[o.key] || []);
      for (var i = 0; i < o.textInputs; i++) if (!String(tx[i] || "").trim()) { out.push({ field: "opt:" + o.key + ":" + i, reason: "required", label: o.label }); break; }
    });
    return out;
  }

  // 区块清单。顺序即两页的显示顺序；visible=false 的区块两页都不出。
  // content 只放「显示什么」的事实（金额、行、文案键），不放 HTML。
  function buildOrderView(p, s, ctx) {
    s = s || {}; ctx = ctx || {};
    var q = quote(p, s, ctx), m = modesOf(p), can = !p.noMode;
    var blocks = [];
    function add(type, visible, content) { blocks.push({ type: type, visible: !!visible, content: content || {} }); }
    add("prices", m.rent || m.buy, {
      rent: m.rent ? { price: p.rent.base.price } : null,
      buy: m.buy ? { price: p.buy.price, compareAt: p.buy.compareAt, offPct: p.buy.compareAt ? Math.round((p.buy.compareAt - p.buy.price) * 100 / p.buy.compareAt) : 0, mto: p.mto } : null,
    });
    var statusRows = [];
    var stVal = p.status || ctx.statusValue, cdVal = p.condition || ctx.conditionValue;
    if (ctx.showStatus !== false && stVal) statusRows.push({ key: "status", noun: p.noun, value: stVal });
    if (ctx.showStatus !== false && cdVal) statusRows.push({ key: "condition", value: cdVal });
    if (p.availability) statusRows.push({ key: "availability", value: p.availability });
    add("status", statusRows.length, { rows: statusRows });
    add("tabs", can, { rent: m.rent, buy: m.buy, active: q.mode, mto: p.mto });
    var fields = [];
    if (q.mode === "rent") fields = ["wedding", "arrival", "return", "package", "func", "country"];
    else if (q.mode === "buy") fields = ["wedding", "estArrival", "func", "country"];
    add("order-specs", can, { fields: fields, packages: p.rent ? p.rent.variants.map(function (v) { return { id: v.id, term: v.term, days: v.days, price: v.price, ext: v.price - p.rent.base.price, extraDays: (v.days != null && p.rent.baseDays != null) ? v.days - p.rent.baseDays : 0, base: v === p.rent.base }; }) : [], planId: q.plan ? q.plan.id : null });
    add("size-ref", p.canAlter, { table: p.sizeTable });
    add("custom-size", can && p.canAlter, { rows: p.canAlter ? measureRows(p, ctx) : [], alterationPrice: num(ctx.alterationPrice) || 1000 });
    add("options", can && p.options.length > 0, { noun: p.noun, kind: p.kind, options: p.options });
    add("est-total", can, q);
    add("buttons", can, { installment: !!ctx.instOn, addToCart: true });
    add("supports", true, {});
    add("accordions", true, {});
    return { version: VERSION, mode: q.mode, modes: m, noMode: p.noMode, blocks: blocks, totals: q, missing: can ? missing(p, s, ctx) : [{ field: "mode", reason: "unavailable" }] };
  }

  // ---------------------------------------------------------------- 购物车行
  // ctx 另需: { bundle, alterationVariant, depositVariant, addonVariant, depUnitMinor, addonUnitMinor, money:function(n)→"HKD 1,000",
  //   rate }  rate = 传入金额 → 购物车结算货币的换算率（商品页传 presentment 价，rate=1；试穿页传 HKD，rate=Shopify.currency.rate），
  //   只用于押金、加价两行按单价凑数量。
  // 同一件商品在两页加购，得到的行与 properties 完全一致。
  // 押金固定变体里挑金额最接近目标的那个（价格是访客货币最小单位）。港币时须分毫不差；别的货币是 Shopify 各自换算取整，
  // 允许差一个货币单位或 1%（两个相邻押金金额至少差约 1%，不会挑错档）。挑不到返回 null。
  function pickFixedDeposit(list, target) {
    if (!list || !list.length || !(target > 0)) return null;
    var best = null, bd = Infinity;
    list.forEach(function (v) { var d = Math.abs(Number(v.price) - target); if (d < bd) { bd = d; best = v; } });
    return best && bd <= Math.max(100, Math.round(target * 0.01)) ? best : null;
  }
  function buildCartLines(p, s, ctx) {
    s = s || {}; ctx = ctx || {};
    var q = quote(p, s, ctx), d = s.dates || {};
    if (!q.mode) return [];
    var fmt = typeof ctx.money === "function" ? ctx.money : function (n) { return String(round(n)); };
    var bundle = ctx.bundle || "b0";
    var rate = num(ctx.rate) || 1;
    var orderType = q.mode === "rent" ? "Rent" : p.buy.orderType;
    var props = { "Order Type": orderType, "_bundle": bundle };
    if (q.mode === "rent") {
      props["Rental Package"] = q.plan.term;
      if (d.wedding) props["Wedding Date"] = d.wedding;
      if (d.arrival) props["Gown Arrival Date"] = d.arrival;
      if (d["return"]) props["Return Date"] = d["return"];
    } else {
      if (d.wedding) props["Wedding Date"] = d.wedding;
      if (d.estArrival) props["Estimated Arrival Date"] = d.estArrival;
    }
    if (s.func) props["Gown Function"] = s.func;
    if (s.country) props["Country"] = s.countryName || s.country;
    if (s.region) props["Region"] = s.region;
    if (p.canAlter) measureRows(p, ctx).forEach(function (r) {
      var a = (s.alts || {})[r.nr];
      if (a && (r.inc || a.checked) && String(a.value == null ? "" : a.value).trim()) props[r.nr + ". " + r.label] = String(a.value).trim() + " cm";
    });
    q.optItems.forEach(function (o) {
      props[o.label] = "+" + fmt(o.upcharge);
      if (o.textInputs) { var tx = ((s.optText || {})[o.key] || []).map(function (x) { return String(x || "").trim(); }).filter(Boolean).join(" & "); if (tx) props["Custom " + cap(o.key)] = tx; }
    });
    if (q.fabric) props["Fabric"] = q.fabricLabel + " +" + fmt(q.fabric);
    var gownVariant = q.mode === "rent" ? q.plan.id : p.buy.variant.id;
    var lines = [{ id: gownVariant, quantity: 1, properties: props }];
    var forGown = p.title + " · " + (q.mode === "rent" ? q.plan.term : orderType) + (d.wedding ? " · Wedding " + d.wedding : "");
    if (q.altItems.length && ctx.alterationVariant) {
      lines.push({ id: ctx.alterationVariant, quantity: q.altItems.length, properties: { "_bundle": bundle, "For Gown": forGown, "Items": q.altItems.map(function (a) { return a.label; }).join(", ") } });
    }
    // 加价选项与面料：一行凑量商品（单价 = addonUnitMinor），按金额凑数量。
    var up = q.options + q.fabric;
    if (up > 0 && ctx.addonVariant) {
      var det = q.optItems.map(function (o) { return o.label + " +" + fmt(o.upcharge); });
      if (q.fabric) det.push("Fabric: " + q.fabricLabel + " +" + fmt(q.fabric));
      lines.push({ id: ctx.addonVariant, quantity: Math.max(1, Math.round(round(up * rate * 100) / (ctx.addonUnitMinor || 100))), properties: { "_bundle": bundle, "For Gown": forGown, "Add-ons": det.join(" / ") } });
    }
    if (q.mode === "rent" && q.deposit > 0 && ctx.depositVariant) {
      var depTarget = Math.round(round(q.deposit * rate * 100)); // 押金目标金额（访客货币的最小单位，如 HKD 7,000 = 700000）
      var depProps = { "_bundle": bundle, "_deposit": "1",
        // 10-09：加购后按购物车里的真实单价把数量校正到这个目标（reconcileDeposits），不再只信页面开头取到的单价
        "_dep_target": depTarget + "|" + (ctx.currency || ""),
        "For Gown": forGown, "Deposit": q.depositPct + "% of base gown price" };
      // 10-12：优先用按金额的固定变体（数量 1，结账页不再显示上千件）；没有对得上的就退回「单价 1 × 件数」，并报缺让后台补
      var fx = pickFixedDeposit(ctx.depositFixed, depTarget);
      if (fx) { depProps["_dep_fixed"] = "1"; lines.push({ id: fx.id, quantity: 1, properties: depProps }); }
      else {
        if (typeof ctx.reportMissingDeposit === "function" && (!ctx.currency || ctx.currency === "HKD")) ctx.reportMissingDeposit(Math.round(depTarget / 100));
        lines.push({ id: ctx.depositVariant, quantity: Math.max(1, Math.round(depTarget / (ctx.depUnitMinor || 100))), properties: depProps });
      }
    }
    return lines;
  }

  // 说明提示（ⓘ）内容，商品页与试穿抽屉共用（数据见 snippets/tcw-notice-data.liquid）。
  // 10-07 owner：只显示设置里填了的——标题留空就没有标题和分隔线，没图就没图，正文留空就没正文；不再有内建文字或商品图兜底。
  // body 是店家在主题设置里写的富文本（原样用，链接统一新标签页）。
  function richLinks(html) { return String(html || "").trim().replace(/<a\s/gi, '<a target="_blank" rel="noopener" '); }
  function noticeHtml(n, _unused, bulbSrc) {
    if (!n) return "";
    var body = richLinks(n.body);
    return (n.title ? '<h2 class="tcw-tip__title">' + n.title + "</h2>" +
        '<div class="tcw-tip__divider" aria-hidden="true"><span class="tcw-tip__line"></span>' + (bulbSrc ? '<img class="tcw-tip__bulb" src="' + bulbSrc + '" alt="" width="30" height="30">' : "") + '<span class="tcw-tip__line"></span></div>' : "") +
      (n.img ? '<div class="tcw-tip__media"><img src="' + n.img + '" alt=""></div>' : "") +
      (body ? '<div class="tcw-tip__body rte">' + body + "</div>" : "");
  }
  // 黑色小框（type = hover）的内容：标题 + 正文，同样只出填了的
  function tipBoxHtml(n) {
    if (!n) return "";
    var body = richLinks(n.body);
    return (n.title ? '<p class="tcw-tooltip-title">' + n.title + "</p>" : "") + (body ? '<div class="tcw-tooltip-body rte">' + body + "</div>" : "");
  }
  // 尺寸写成 CSS 变量挂在弹层上：--np-w 宽(px) / --np-h 电脑最大高(%屏高) / --np-hm 手机最大高 / --np-imgw 图宽(%正文宽)
  function noticeStyle(n) {
    if (!n) return "";
    return "--np-w:" + (+n.w || 600) + "px;--np-h:" + (+n.h || 88) + "vh;--np-hm:" + (+n.hm || 82) + "dvh;--np-imgw:" + (+n.imgw || 75) + "%";
  }
  // ⓘ 出不出只看开关（10-07 owner：开关控制，不看有没有内容）
  function noticeHas(n) { return !!(n && n.on !== false); }

  // 押金行数量校正（10-09 / 10-10）。押金 = 单价 1 的 Security Deposit × 数量，数量只在加购那一刻算。
  // 10-10 查明的真正原因：客人以 International（如新币，押金单价被货币取整成 S$1）加购后切到香港，
  // 礼服按香港价格表重新定价，押金件数却不变（1,222 件 × HK$1）；反方向（港币加购后切新币）则会多收数倍。
  // 所以按「当前货币」重算：应收 = round(礼服在当前货币下最便宜的 Rent 价 × 押金行写明的比例)，与商品页 quote() 同一规则；
  // 取不到商品价时退回加购时记下的 _dep_target（货币一致才用）。数量不对就 /cart/change.js 改。
  // 返回 Promise<sections | null>：改过给新的 sections（没传 sectionsId 时给 {}），没改给 null。
  function depositWant(it, gown, prodJs, cartCurrency) {
    var pr = it.properties || {}, m = String(pr.Deposit || "").match(/(\d+)\s*%/), pct = m ? Number(m[1]) : 0;
    if (pct > 0 && gown && prodJs && prodJs.variants) {
      var p = normalizeProduct({ variants: prodJs.variants.map(function (v) { return { id: v.id, title: v.title, price: v.price / 100 }; }) });
      if (p.rent && p.rent.base) return round(p.rent.base.price * pct / 100) * 100;
    }
    var t = String(pr._dep_target || "").split("|"), target = Number(t[0]), cur = t[1] || "";
    if (target > 0 && (!cur || !cartCurrency || cur === cartCurrency)) return target;
    return 0;
  }
  function reconcileDeposits(sectionsId, fetchFn) {
    var F = fetchFn || (typeof fetch === "function" ? fetch : null);
    if (!F) return Promise.resolve(null);
    var getJson = function (u) { return F(u, { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); }); };
    return getJson("/cart.js").then(function (cart) {
      var items = cart.items || [];
      // 固定金额变体（_dep_fixed）本身就是一个有价格的商品，换货币由 Shopify 跟礼服一起换算，数量永远是 1；只校正旧的「单价 1 × 件数」行
      var fixedWrong = items.filter(function (it) { return (it.properties || {})._dep_fixed === "1" && it.quantity !== 1; });
      var deps = items.filter(function (it) { return (it.properties || {})._deposit === "1" && (it.properties || {})._dep_fixed !== "1" && it.price > 0; });
      if (!deps.length && !fixedWrong.length) return null;
      var gownOf = function (d) {
        var bd = (d.properties || {})._bundle;
        return items.filter(function (it) { var p = it.properties || {}; return p._bundle === bd && p._deposit !== "1" && /rent/i.test(String(p["Order Type"] || "")); })[0] || null;
      };
      var handles = {};
      deps.forEach(function (d) { var g = gownOf(d); if (g && g.handle) handles[g.handle] = null; });
      return Promise.all(Object.keys(handles).map(function (h) {
        return getJson("/products/" + encodeURIComponent(h) + ".js").then(function (j) { handles[h] = j; }).catch(function () { handles[h] = null; });
      })).then(function () {
        var fixes = fixedWrong.map(function (it) { return { key: it.key, quantity: 1 }; });
        deps.forEach(function (d) {
          var g = gownOf(d), want = depositWant(d, g, g && handles[g.handle], cart.currency);
          if (!(want > 0)) return;
          var q = Math.max(1, Math.round(want / d.price));
          if (q !== d.quantity) fixes.push({ key: d.key, quantity: q });
        });
        if (!fixes.length) return null;
        var last = null;
        return fixes.reduce(function (p, fx) {
          return p.then(function () {
            var body = { id: fx.key, quantity: fx.quantity };
            if (sectionsId) { body.sections = sectionsId; body.sections_url = (typeof location !== "undefined" ? location.pathname : "/"); }
            return F("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body) })
              .then(function (r) { return r.json(); }).then(function (j) { last = j; });
          });
        }, Promise.resolve()).then(function () { return (last && last.sections) || {}; });
      });
    }).catch(function () { return null; });
  }

  return {
    VERSION: VERSION,
    normalizeProduct: normalizeProduct,
    measureRows: measureRows,
    resolveMode: resolveMode,
    depositPctOf: depositPctOf,
    quote: quote,
    missing: missing,
    buildOrderView: buildOrderView,
    buildCartLines: buildCartLines,
    noticeHtml: noticeHtml,
    noticeStyle: noticeStyle,
    noticeHas: noticeHas,
    tipBoxHtml: tipBoxHtml,
    reconcileDeposits: reconcileDeposits,
    pickFixedDeposit: pickFixedDeposit,
  };
});
